/* COLESICE theme: shared cart behaviour (cart drawer and cart page).
   - Quantity change, remove, order note, discount codes and drawer recommendations (delegated, work after every re-render)
   - <cart-timer>, <cart-timer-reset>, <cs-shipping-meter> custom elements
   - Reloads every [data-cart-section] when an app changed the cart (event cart:refresh) and then fires cart:refreshed.
   The re-rendering itself (after each Theme.cart call) is done by theme.js. */

const Theme = window.Theme;
const strings = () => (Theme && Theme.config && Theme.config.strings) || {};

/* ---------- Reservation timer ---------- */
const DEADLINE_KEY = 'cart_reservation_deadline';
const readDeadline = () => {
  try { return parseInt(localStorage.getItem(DEADLINE_KEY), 10) || 0; } catch (e) { return 0; }
};
const writeDeadline = (value) => {
  try { localStorage.setItem(DEADLINE_KEY, String(value)); } catch (e) { /* storage blocked: the timer still runs for this page */ }
};
const clearDeadline = () => {
  try { localStorage.removeItem(DEADLINE_KEY); } catch (e) { /* ignore */ }
};
let memoryDeadline = 0;

class CartTimer extends HTMLElement {
  connectedCallback() {
    this.minutesEl = this.querySelector('[data-timer-minutes]');
    this.secondsEl = this.querySelector('[data-timer-seconds]');
    this.duration = Math.max(1, Number(this.getAttribute('minutes')) || 10) * 60000;
    this.onVisibility = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this.onVisibility);
    this.start();
  }
  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }
  start() {
    this.stop();
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }
  stop() {
    clearInterval(this.interval);
    this.interval = null;
  }
  remaining() {
    let deadline = readDeadline() || memoryDeadline;
    const now = Date.now();
    // no deadline yet, it ran out (start again) or the minutes setting became shorter
    if (!deadline || deadline <= now || deadline - now > this.duration) {
      deadline = now + this.duration;
      memoryDeadline = deadline;
      writeDeadline(deadline);
    }
    return deadline - now;
  }
  tick() {
    const remaining = this.remaining();
    const total = Math.floor(remaining / 1000);
    const pad = (n) => String(n).padStart(2, '0');
    if (this.minutesEl) this.minutesEl.textContent = pad(Math.floor(total / 60));
    if (this.secondsEl) this.secondsEl.textContent = pad(total % 60);
    this.classList.toggle('is-urgent', remaining <= Math.min(120000, this.duration * 0.2));
  }
}
if (!customElements.get('cart-timer')) customElements.define('cart-timer', CartTimer);

// Rendered inside an empty cart: forgets the deadline so the next cart starts a fresh countdown.
class CartTimerReset extends HTMLElement {
  connectedCallback() {
    memoryDeadline = 0;
    clearDeadline();
  }
}
if (!customElements.get('cart-timer-reset')) customElements.define('cart-timer-reset', CartTimerReset);

/* ---------- Free shipping bar ---------- */
class FreeShippingBar extends HTMLElement {
  connectedCallback() { this.update(); }
  update() {
    const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || Number(Theme.config.currencyRate) || 1;
    // Shop currency (rate 1): unchanged. Other currencies: converted, then rounded UP to a whole unit, like the markets'
    // "round prices up after conversion" setting, so the amount matches the rounded shelf prices.
    const converted = Number(this.dataset.threshold) * rate;
    const threshold = rate === 1 ? Math.round(converted) : Math.ceil(converted / 100 - 1e-6) * 100;
    const total = Number(this.dataset.total) || 0;
    if (!threshold) return;
    const remaining = Math.max(0, threshold - total);
    const reached = remaining <= 0;
    const message = this.querySelector('[data-message]');
    const fill = this.querySelector('[data-fill]');
    const track = this.querySelector('[role="progressbar"]');
    const progress = Math.min(1, Math.max(0, total / threshold));
    if (message) message.innerHTML = reached ? this.dataset.reached : (this.dataset.unreached || '').replace('[[amount]]', Theme.formatMoney(remaining));
    if (fill) fill.style.setProperty('--progress', String(progress));
    if (track) track.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
    this.classList.toggle('is-reached', reached);
  }
}
if (!customElements.get('cs-shipping-meter')) customElements.define('cs-shipping-meter', FreeShippingBar);

/* ---------- Helpers ---------- */
const cartRoot = (el) => el.closest('[data-cart-section]');

// Locks a cart UI while a request runs (one change at a time keeps the line numbers valid).
async function whileUpdating(root, task) {
  if (!root || root.classList.contains('is-updating')) return undefined;
  root.classList.add('is-updating');
  try { return await task(); } finally { root.classList.remove('is-updating'); }
}

function showLineError(line, message) {
  const el = line && line.querySelector('[data-line-error]');
  if (!el) return;
  el.textContent = message || '';
  el.hidden = !message;
}

/* ---------- Quantity and remove ---------- */
async function changeLine(trigger, quantity) {
  const line = trigger.closest('[data-cart-line]');
  const root = cartRoot(trigger);
  if (!line || !root) return;
  const key = line.dataset.key;
  const index = Number(line.dataset.cartLine);
  const keepFocus = trigger.matches('[data-cart-quantity]') && quantity > 0;
  await whileUpdating(root, async () => {
    try {
      await Theme.cart.change(index, quantity, 'cart-line');
      if (keepFocus) {
        const next = [...root.querySelectorAll('[data-cart-line]')].find((el) => el.dataset.key === key);
        const field = next && next.querySelector('[data-cart-quantity]');
        if (field) field.focus({ preventScroll: true });
      }
    } catch (error) {
      if (trigger.matches('input')) trigger.value = trigger.defaultValue;
      showLineError(line, (error && error.message) || strings().quantityError);
    }
  });
}

document.addEventListener('change', (event) => {
  const input = event.target.closest && event.target.closest('[data-cart-quantity]');
  if (!input) return;
  const line = input.closest('[data-cart-line]');
  const quantity = parseInt(input.value, 10);
  showLineError(line, '');
  if (Number.isNaN(quantity) || quantity < 0) { input.value = input.defaultValue; return; }
  if (input.max && quantity > Number(input.max)) {
    input.value = input.defaultValue;
    showLineError(line, strings().quantityError);
    return;
  }
  if (String(quantity) === input.defaultValue) return;
  changeLine(input, quantity);
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter') return;
  if (event.target.closest('[data-cart-quantity]')) {
    event.preventDefault(); // Enter must not submit the checkout form
    event.target.blur();
  } else if (event.target.closest('[data-discount-input]')) {
    event.preventDefault();
    applyDiscount(event.target);
  }
});

document.addEventListener('click', (event) => {
  const remove = event.target.closest('[data-cart-remove]');
  if (remove && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
    event.preventDefault();
    changeLine(remove, 0);
    return;
  }
  const noteSave = event.target.closest('[data-cart-note-save]');
  if (noteSave) {
    const field = cartRoot(noteSave) && cartRoot(noteSave).querySelector('textarea[data-cart-note]');
    if (field) saveNote(field);
    return;
  }
  const apply = event.target.closest('[data-discount-apply]');
  if (apply) {
    const input = apply.closest('[data-cart-discount]').querySelector('[data-discount-input]');
    applyDiscount(input);
    return;
  }
  const removeDiscount = event.target.closest('[data-discount-remove]');
  if (removeDiscount) {
    const root = cartRoot(removeDiscount);
    const input = root && root.querySelector('[data-discount-input]');
    const codes = ((input && input.dataset.codes) || '').split(',').filter(Boolean);
    const next = codes.filter((code) => code.toLowerCase() !== removeDiscount.dataset.discountRemove.toLowerCase());
    whileUpdating(root, () => Theme.cart.update({ discount: next.join(',') }, 'cart-discount').catch(() => {}));
  }
});

/* ---------- Order note ---------- */
function saveNote(field) {
  const root = cartRoot(field);
  return whileUpdating(root, () => Theme.cart.update({ note: field.value }, 'cart-note').catch(() => {}));
}
// On the cart page the note is saved when the field is left (the checkout button posts it as well).
document.addEventListener('change', (event) => {
  const field = event.target.closest && event.target.closest('.main-cart__summary textarea[data-cart-note]');
  if (field) saveNote(field);
});

/* ---------- Discount codes ---------- */
async function applyDiscount(input) {
  const root = cartRoot(input);
  const code = input.value.trim();
  if (!root || !code) return;
  const previous = (input.dataset.codes || '').split(',').filter(Boolean);
  if (previous.some((c) => c.toLowerCase() === code.toLowerCase())) { input.value = ''; return; }
  const fail = () => {
    const error = root.querySelector('[data-discount-error]');
    if (error) error.hidden = false;
  };
  await whileUpdating(root, async () => {
    try {
      const cart = await Theme.cart.update({ discount: [...previous, code].join(',') }, 'cart-discount');
      const found = (cart.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
      if (!found || !found.applicable) {
        await Theme.cart.update({ discount: previous.join(',') }, 'cart-discount');
        fail();
      }
    } catch (error) {
      fail();
    }
  });
}

/* ---------- Recommendations in the drawer ---------- */
document.addEventListener('submit', async (event) => {
  const form = event.target.closest && event.target.closest('form[data-cart-recommendation]');
  if (!form) return;
  event.preventDefault();
  const button = form.querySelector('button[type="submit"]');
  const old = form.querySelector('.cart-recs__error');
  if (old) old.remove();
  if (button) { button.disabled = true; button.classList.add('is-loading'); }
  try {
    await Theme.cart.add(new FormData(form), 'cart-recommendation');
  } catch (error) {
    if (button) { button.disabled = false; button.classList.remove('is-loading'); }
    const p = document.createElement('p');
    p.className = 'cart-recs__error fs-sm tone-error';
    p.setAttribute('role', 'alert');
    p.textContent = (error && error.message) || strings().cartError;
    form.append(p);
  }
});

/* ---------- Apps changed the cart: reload every cart UI ---------- */
const cartSectionIds = () => [...new Set([...document.querySelectorAll('[data-cart-section]')].map((el) => el.dataset.cartSection))];
let refreshing = false;
document.addEventListener('cart:refresh', async () => {
  const ids = cartSectionIds();
  if (!ids.length || refreshing) return;
  refreshing = true;
  try {
    const [cart, sections] = await Promise.all([Theme.cart.get(), Theme.fetchSections(location.pathname + location.search, ids)]);
    Theme.renderCartSections(sections);
    Theme.emit('cart:refreshed', { cart });
  } catch (error) {
    /* the cart stays as it is */
  } finally {
    refreshing = false;
  }
});

// Coming back from checkout with the back button shows a stored page: make sure the cart is current.
window.addEventListener('pageshow', (event) => { if (event.persisted) Theme.emit('cart:refresh'); });
